/**
 * Pure render-to-message handlers for ChatBox's informational slash commands.
 *
 * `/conversation` and `/conversations` build a markdown summary from the current
 * conversation state and append it locally via `addLocalMessages` — they never
 * touch the network. `/search` posts a "Searching..." message, runs a hybrid
 * search through the typed `api` helper, and renders the results (with a
 * "Google Drive" source badge for Drive-sourced objects).
 */
import { api } from '@/lib/api';
import type { Conversation } from '@/types/conversations';
import type { ConversationJson } from '@/lib/query-engine/types';

/**
 * Signature of the `addLocalMessages` callback surfaced by `useChat`
 * (see `useChat.types.ts` → `UseChatReturn.addLocalMessages`). Reproduced here
 * so the extracted handlers stay fully typed without importing the whole hook.
 */
type AddLocalMessagesFn = (
  userContent: string,
  assistantContent: string,
  conversationId?: string,
  metadata?: {
    routing?: string;
    durationMs?: number;
    intent?: string;
  },
) => {
  userMessageId?: string;
  assistantMessageId: string;
};

/**
 * Dependencies captured by ChatBox's `handleConversationCommand` callback.
 */
export interface RunConversationCommandDeps {
  activeConversation: Conversation | null;
  conversationJson: ConversationJson | undefined;
  addLocalMessages: AddLocalMessagesFn;
}

/** Render the active conversation's name + object counts as a local message. */
export function runConversationCommand({
  activeConversation,
  conversationJson,
  addLocalMessages,
}: RunConversationCommandDeps): void {
  if (!activeConversation) {
    addLocalMessages('/conversation', 'No conversation selected. Select one from the sidebar to see its info.', undefined, {
      routing: 'command:conversation',
      durationMs: 0,
    });
    return;
  }

  const objectCounts: string[] = [];
  if (conversationJson?.objects) {
    const types = ['task', 'contact', 'event', 'note', 'place', 'asset', 'stat'];
    types.forEach(type => {
      const count = conversationJson.objects[type]?.length || 0;
      if (count > 0) {
        objectCounts.push(`${count} ${type}${count !== 1 ? 's' : ''}`);
      }
    });
  }

  const conversationInfo = `**${activeConversation.name}**

${objectCounts.length > 0 ? `**Contents:** ${objectCounts.join(', ')}` : '*No objects yet*'}`;

  addLocalMessages('/conversation', conversationInfo, activeConversation.id, {
    routing: 'command:conversation',
    durationMs: 0,
  });
}

/**
 * Dependencies captured by ChatBox's `handleConversationsCommand` callback.
 */
export interface RunConversationsCommandDeps {
  allConversations: Conversation[];
  activeConversation: Conversation | null;
  addLocalMessages: AddLocalMessagesFn;
}

/** Render the full conversation list as a local message, marking the active one. */
export function runConversationsCommand({
  allConversations,
  activeConversation,
  addLocalMessages,
}: RunConversationsCommandDeps): void {
  if (allConversations.length === 0) {
    addLocalMessages('/conversations', 'You don\'t have any conversations yet. Create one to get started!', undefined, {
      routing: 'command:conversations',
      durationMs: 0,
    });
    return;
  }

  const conversationList = allConversations.map(conversation => {
    const isActive = conversation.id === activeConversation?.id;
    return `- ${isActive ? '**' : ''}${conversation.name}${isActive ? '** (current)' : ''}`;
  }).join('\n');

  const response = `**Your Conversations** (${allConversations.length})\n\n${conversationList}\n\n*Click a conversation in the sidebar to switch.*`;

  addLocalMessages('/conversations', response, activeConversation?.id, {
    routing: 'command:conversations',
    durationMs: 0,
  });
}

/**
 * Dependencies captured by ChatBox's `handleSearchCommand` callback.
 */
export interface RunSearchCommandDeps {
  api: typeof api;
  token: string | null;
  activeConversation: Conversation | null;
  addLocalMessages: AddLocalMessagesFn;
}

/**
 * Run a hybrid search for `query` and render the results as a local message:
 * posts a "Searching..." placeholder, calls `api.search.hybridSearch`, formats
 * hits into markdown (surfacing a "Google Drive" badge for Drive-sourced
 * objects), and handles the empty + error cases with timing.
 */
export async function runSearchCommand(
  query: string,
  { api, token, activeConversation, addLocalMessages }: RunSearchCommandDeps,
): Promise<void> {
  if (!query.trim()) {
    addLocalMessages('/search', 'Usage: `/search <query>`\n\nExample: `/search quarterly report`', activeConversation?.id, {
      routing: 'command:search',
      durationMs: 0,
    });
    return;
  }

  addLocalMessages(`/search ${query}`, 'Searching...', activeConversation?.id, {
    routing: 'command:search',
    durationMs: 0,
  });

  const startTime = Date.now();

  try {
    const results = await api.search.hybridSearch(token, query.trim(), {
      conversationIds: activeConversation?.id ? [activeConversation.id] : undefined,
      limit: 10,
    });

    const durationMs = Date.now() - startTime;

    if (!results.results || results.results.length === 0) {
      addLocalMessages(`/search ${query}`, `No results found for "${query}"${activeConversation ? ` in ${activeConversation.name}` : ''}.`, activeConversation?.id, {
        routing: 'command:search',
        durationMs,
      });
      return;
    }

    // Drive-cached docx rows (attributes.source === 'google_drive', see
    // packages/persistence insertDriveCachedDocx) are stored as note objects but
    // originate from Drive. The mapper spreads attributes into `object` (see
    // apps/api/src/mappers/object-mapper.ts), so `source` is on every result.
    // Surface a "Google Drive" badge for parity with the LLM-side label in
    // apps/x-agent/src/workspace-index.ts formatMatchesContext.
    const resultList = results.results.map((r: any, i: number) => {
      const obj = r.object || r; // Handle both nested and flat structures
      const type = obj.type ? obj.type.charAt(0).toUpperCase() + obj.type.slice(1) : 'Item';
      const title = obj.title || obj.name || 'Untitled';
      let sourceBadge = '';
      if (typeof obj.source === 'string' && obj.source.startsWith('google_drive')) {
        const email = typeof obj.integrationEmail === 'string' && obj.integrationEmail.length > 0
          ? obj.integrationEmail
          : null;
        sourceBadge = email ? ` _[Google Drive — ${email}]_` : ' _[Google Drive]_';
      }
      return `${i + 1}. **${title}** (${type})${sourceBadge}`;
    }).join('\n\n');

    const response = `**Search Results** for "${query}"${activeConversation ? ` in ${activeConversation.name}` : ''}\n\n${resultList}\n\n*Found ${results.results.length} result${results.results.length !== 1 ? 's' : ''} in ${durationMs}ms*`;

    addLocalMessages(`/search ${query}`, response, activeConversation?.id, {
      routing: 'command:search',
      durationMs,
    });
  } catch (error) {
    const durationMs = Date.now() - startTime;
    addLocalMessages(`/search ${query}`, `Search failed: ${error instanceof Error ? error.message : 'Unknown error'}`, activeConversation?.id, {
      routing: 'command:search',
      durationMs,
    });
  }
}

// ============================================================================
// Quick-create commands (/task, /note, /contact, /event)
// ============================================================================

/**
 * Object types creatable via the quick-create slash commands. Mirrors the
 * inline `PendingModuleSelection.objectType` union in ChatBox.tsx and the first
 * parameter of `completeObjectCreation`.
 */
export type CreateCommandObjectType = 'task' | 'note' | 'contact' | 'event';

/** A conversation module candidate, as returned by `findModulesByObjectType`. */
export interface CreateCommandModule {
  id: string;
  label: string;
}

/**
 * Pending module-selection state ChatBox stores when more than one module
 * matches the requested object type. Declared structurally (not imported)
 * because ChatBox's `PendingModuleSelection` is local to the component body.
 */
export interface CreateCommandPendingSelection {
  objectType: CreateCommandObjectType;
  // Light-parser output is intentionally `any` here to mirror the original
  // `PendingModuleSelection.parsedData` / `completeObjectCreation` typing.
   
  parsedData: any;
  modules: CreateCommandModule[];
  originalInput: string;
}

/**
 * Dependencies + per-type parameters for the quick-create handlers
 * (`/task`, `/note`, `/contact`, `/event`). Everything that varies between the
 * four object types is passed in so one factory serves all of them.
 */
export interface RunCreateCommandDeps {
  /** Object type — drives routing strings, the pending-selection payload, and `completeObjectCreation`. */
  objectType: CreateCommandObjectType;
  /** Command prefix shown in the echoed user message, e.g. `/task`. */
  commandPrefix: string;
  /** Verbatim usage-help text shown when input is empty. */
  usageHelp: string;
  /** Noun used in the multi-module prompt question ("Which module should this <noun> go in?"). Note: `/note` uses "doc". */
  promptQuestionNoun: string;
  /** Noun used in the multi-module prompt click hint ("...create the <noun>."). */
  promptClickNoun: string;
  /** Fallback module name when the single matched module has no label, e.g. `Tasks`. */
  fallbackModuleName: string;
  /** Verbatim message shown when no module of this type exists in the conversation. */
  noModuleMessage: string;
  /** Light parser for this object type, e.g. `parseTaskInput`. */
   
  parse: (input: string) => any;
  findModulesByObjectType: (objectType: string) => CreateCommandModule[];
  completeObjectCreation: (
    objectType: CreateCommandObjectType,
     
    parsedData: any,
    moduleId: string,
    moduleName: string,
    originalInput: string,
  ) => Promise<void>;
  setPendingModuleSelection: (selection: CreateCommandPendingSelection) => void;
  addLocalMessages: AddLocalMessagesFn;
  activeConversation: Conversation | null;
}

/**
 * Parameterized quick-create handler shared by `/task`, `/note`, `/contact`,
 * and `/event`: shows usage-help on empty input, parses the input with the
 * per-type light parser, finds matching conversation modules, and either prompts
 * the user to pick a module (when >1 match), creates directly (when exactly 1),
 * or reports that no module exists. All per-type wording is supplied via `deps`.
 */
export async function runCreateCommand(
  input: string,
  {
    objectType,
    commandPrefix,
    usageHelp,
    promptQuestionNoun,
    promptClickNoun,
    fallbackModuleName,
    noModuleMessage,
    parse,
    findModulesByObjectType,
    completeObjectCreation,
    setPendingModuleSelection,
    addLocalMessages,
    activeConversation,
  }: RunCreateCommandDeps,
): Promise<void> {
  if (!input.trim()) {
    addLocalMessages(commandPrefix, usageHelp, activeConversation?.id, {
      routing: `command:${objectType}`,
      durationMs: 0,
    });
    return;
  }

  const parsed = parse(input);
  const modules = findModulesByObjectType(objectType);

  if (modules.length > 1) {
    setPendingModuleSelection({
      objectType,
      parsedData: parsed,
      modules,
      originalInput: input,
    });

    const moduleList = modules.map((m, i) => `${i + 1}. ${m.label}`).join('\n');
    addLocalMessages(`${commandPrefix} ${input}`, `**Which module should this ${promptQuestionNoun} go in?**\n\n${moduleList}\n\n*Click a module below to create the ${promptClickNoun}.*`, activeConversation?.id, {
      routing: `command:${objectType}:select-module`,
      durationMs: 0,
    });
    return;
  }

  // Single or no module — proceed directly
  const moduleId = modules[0]?.id;
  const moduleName = modules[0]?.label || fallbackModuleName;

  if (moduleId) {
    await completeObjectCreation(objectType, parsed, moduleId, moduleName, input);
  } else {
    addLocalMessages(`${commandPrefix} ${input}`, noModuleMessage, activeConversation?.id, {
      routing: `command:${objectType}`,
      durationMs: 0,
    });
  }
}
